import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { Bet } from '../api/types';

interface PaymentConfig {
  method: 'manual_pix' | 'mercado_pago';
  pix_key: string;
  pix_receiver_name: string;
  amount_cents: number;
}

export default function PaymentPage() {
  const { betId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);

  const { data: bet } = useQuery({
    queryKey: ['bet', betId],
    queryFn: async () => (await api.get<Bet>(`/bets/${betId}`)).data,
    refetchInterval: (q) => {
      const b = q.state.data as Bet | undefined;
      return b && b.status !== 'paid' && b.status !== 'draft' ? 5_000 : false;
    },
  });

  const { data: config } = useQuery({
    queryKey: ['payment-config'],
    queryFn: async () => (await api.get<PaymentConfig>('/payment-config')).data,
  });

  // Dispara o submit automaticamente se o bet ainda está como draft
  const submit = useMutation({
    mutationFn: async () => (await api.post<Bet>(`/bets/${betId}/submit`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bet', betId] }),
  });

  const markPaid = useMutation({
    mutationFn: async () => (await api.post<Bet>(`/bets/${betId}/mark-paid`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bet', betId] }),
  });

  // Dispara o submit uma única vez quando o bet ainda não tem QR pronto
  useEffect(() => {
    if (!bet || !config) return;
    if (!submit.isIdle) return;
    const needsSubmit = bet.status === 'draft';
    const needsMpRetry =
      bet.status === 'awaiting_payment' &&
      config.method === 'mercado_pago' &&
      !bet.pix_qr_code;
    if (needsSubmit || needsMpRetry) {
      submit.mutate();
    }
  }, [bet, config, submit]);

  if (!bet || !config) return <div>Carregando…</div>;

  if (submit.isError) {
    const msg =
      (submit.error as any)?.response?.data?.message ||
      (submit.error as Error)?.message ||
      'Erro ao iniciar pagamento';
    return (
      <div className="card max-w-md mx-auto space-y-3 text-center">
        <h2 className="text-xl font-bold text-red-600">Falha no pagamento</h2>
        <p className="text-sm text-slate-600 break-words">{msg}</p>
        <button className="btn btn-secondary" onClick={() => submit.reset()}>
          Tentar novamente
        </button>
      </div>
    );
  }

  // Status final: pago
  if (bet.status === 'paid') {
    return (
      <div className="card text-center space-y-3 max-w-md mx-auto">
        <h2 className="text-2xl font-bold text-brand-600">Bolão confirmado! 🎉</h2>
        <p className="text-sm text-slate-600">
          Confirmado em {new Date(bet.paid_at!).toLocaleString('pt-BR')}.
        </p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Voltar
        </button>
      </div>
    );
  }

  // Em análise (após user clicar "Já paguei")
  if (bet.status === 'awaiting_manual_confirmation') {
    return (
      <div className="card max-w-md mx-auto space-y-3 text-center">
        <div className="text-3xl">⏳</div>
        <h2 className="text-xl font-bold">Pagamento em análise</h2>
        <p className="text-sm text-slate-600">
          Seu pagamento foi marcado e está em análise pelo administrador.
          Assim que confirmar, seu bolão será travado e você verá aqui o status como pago.
        </p>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Voltar
        </button>
      </div>
    );
  }

  // status === 'draft' → submit foi disparado pelo useEffect acima
  if (bet.status === 'draft') {
    return <div>{bet.amount_cents === 0 ? 'Confirmando seu bolão…' : 'Preparando pagamento…'}</div>;
  }

  // status === 'awaiting_payment' → fluxo ativo (manual OU mercado pago)
  const amount = (config.amount_cents / 100).toFixed(2);

  // MERCADO PAGO
  if (config.method === 'mercado_pago') {
    return (
      <div className="card space-y-3 max-w-md mx-auto text-center">
        <h2 className="text-xl font-bold">Pague com Pix</h2>
        <p className="text-sm">
          Valor: <strong>R$ {amount}</strong>
        </p>
        {bet.pix_qr_code_base64 && (
          <img
            alt="QR Code Pix"
            className="mx-auto w-64 h-64"
            src={`data:image/png;base64,${bet.pix_qr_code_base64}`}
          />
        )}
        {bet.pix_qr_code && (
          <div>
            <label className="block text-xs mb-1">Pix copia e cola</label>
            <textarea
              readOnly
              value={bet.pix_qr_code}
              className="input text-xs h-24"
              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
            />
          </div>
        )}
        <p className="text-xs text-slate-500 animate-pulse">
          Aguardando confirmação automática…
        </p>
        <p className="text-xs text-slate-500 border-t pt-2">
          Se você já pagou um bolão dessa rodada e o app do banco avisar sobre "Pix similar",
          pode confirmar — cada bolão tem um código único e o pagamento vai normalmente.
        </p>
      </div>
    );
  }

  // PIX MANUAL — QR estático + chave Pix + botão "Já paguei"
  async function copyKey() {
    try {
      await navigator.clipboard.writeText(config!.pix_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard pode falhar em http; ignora */
    }
  }

  return (
    <div className="card max-w-md mx-auto space-y-4">
      <div className="text-center">
        <h2 className="text-xl font-bold">Pague com Pix</h2>
        <p className="text-sm mt-1">
          Valor: <strong>R$ {amount}</strong>
        </p>
        {config.pix_receiver_name && (
          <p className="text-xs text-slate-500">Recebedor: {config.pix_receiver_name}</p>
        )}
      </div>

      <div className="flex justify-center">
        <img
          src="/pix-qr.png"
          alt="QR Code Pix"
          className="w-64 h-64 border border-slate-200 rounded"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>

      {config.pix_key && (
        <div>
          <label className="block text-xs mb-1">Chave Pix</label>
          <div className="flex gap-2">
            <input className="input flex-1 text-sm" readOnly value={config.pix_key} />
            <button type="button" className="btn btn-secondary text-sm" onClick={copyKey}>
              {copied ? '✓' : 'Copiar'}
            </button>
          </div>
        </div>
      )}

      <div className="text-xs text-slate-600 bg-slate-100 rounded p-2">
        <strong>Como fazer:</strong> abra o app do seu banco → Pix → ler QR Code (ou colar chave) →
        pague <strong>R$ {amount}</strong> → volte aqui e clique em <em>Já paguei</em>. O admin vai
        confirmar seu pagamento.
      </div>

      <button
        className="btn btn-primary w-full"
        onClick={() => markPaid.mutate()}
        disabled={markPaid.isPending}
      >
        {markPaid.isPending ? 'Enviando…' : 'Já paguei'}
      </button>
    </div>
  );
}
